import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isToday, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  Calendar as CalendarIcon,
  Clock,
  User,
  Loader2,
  X
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }
};

const views = [
  { id: 'month', label: 'Mes' },
  { id: 'week', label: 'Semana' },
  { id: 'day', label: 'Día' },
];

export default function Agenda() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    client_id: '',
    service_id: '',
    staff_id: '',
    date: '',
    time: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [aptsRes, clientsRes, servicesRes, staffRes] = await Promise.all([
        axios.get(`${API}/api/appointments`, { headers }),
        axios.get(`${API}/api/clients`, { headers }),
        axios.get(`${API}/api/services`, { headers }),
        axios.get(`${API}/api/staff`, { headers }),
      ]);
      setAppointments(aptsRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      setStaff(staffRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    if (view === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const handleNext = () => {
    if (view === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setNewAppointment({ ...newAppointment, date: format(date, 'yyyy-MM-dd') });
    setShowModal(true);
  };

  const handleCreateAppointment = async () => {
    if (!newAppointment.client_id || !newAppointment.service_id || !newAppointment.date || !newAppointment.time) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/api/appointments`, newAppointment, { headers });
      setAppointments([...appointments, data]);
      setShowModal(false);
      setNewAppointment({ client_id: '', service_id: '', staff_id: '', date: '', time: '', notes: '' });
      toast.success('Cita creada');
    } catch (err) {
      toast.error('Error al crear cita');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAppointment = async (aptId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/api/appointments/${aptId}`, { headers });
      setAppointments(appointments.filter(a => a.id !== aptId));
      toast.success('Cita eliminada');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(apt => apt.date === dateStr);
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="nova-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-[var(--nova-border)]">
          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day) => (
            <div key={day} className="p-3 text-center text-overline">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((day, i) => {
            const dayAppointments = getAppointmentsForDate(day);
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            
            return (
              <div
                key={i}
                onClick={() => handleDayClick(day)}
                className={`
                  min-h-24 p-2 border-b border-r border-[var(--nova-border-subtle)] cursor-pointer
                  hover:bg-[var(--nova-surface-raised)] transition-colors
                  ${!isCurrentMonth ? 'opacity-30' : ''}
                `}
              >
                <div className={`
                  w-7 h-7 rounded-full flex items-center justify-center text-sm mb-1
                  ${isToday(day) 
                    ? 'bg-[var(--nova-primary)] text-white font-semibold shadow-lg shadow-indigo-500/30' 
                    : 'text-[var(--nova-text-secondary)]'
                  }
                `}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1">
                  {dayAppointments.slice(0, 2).map((apt, j) => {
                    const service = services.find(s => s.id === apt.service_id);
                    return (
                      <div
                        key={j}
                        className="text-xs px-1.5 py-0.5 rounded-md font-medium truncate"
                        style={{ 
                          backgroundColor: `${service?.color || '#6366f1'}15`,
                          color: service?.color || '#6366f1'
                        }}
                      >
                        {apt.time}
                      </div>
                    );
                  })}
                  {dayAppointments.length > 2 && (
                    <div className="text-xs text-[var(--nova-text-quaternary)]">+{dayAppointments.length - 2} más</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const hours = Array.from({ length: 14 }, (_, i) => `${(i + 7).toString().padStart(2, '0')}:00`);

    return (
      <div className="nova-card overflow-hidden">
        <div className="grid grid-cols-8 border-b border-[var(--nova-border)]">
          <div className="p-3" />
          {days.map((day, i) => (
            <div key={i} className={`p-3 text-center border-l border-[var(--nova-border-subtle)] ${isToday(day) ? 'bg-indigo-500/[0.04]' : ''}`}>
              <div className="text-overline">{format(day, 'EEE', { locale: es })}</div>
              <div className={`text-lg font-semibold mt-0.5 ${isToday(day) ? 'text-[var(--nova-primary-light)]' : 'text-[var(--nova-text-secondary)]'}`}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {hours.map((hour) => (
            <div key={hour} className="grid grid-cols-8 border-b border-[var(--nova-border-subtle)]">
              <div className="p-2 text-xs text-[var(--nova-text-quaternary)] text-right pr-3 font-medium">{hour}</div>
              {days.map((day, i) => {
                const dayAppointments = getAppointmentsForDate(day).filter(apt => apt.time.startsWith(hour.split(':')[0]));
                return (
                  <div 
                    key={i} 
                    onClick={() => {
                      setSelectedDate(day);
                      setNewAppointment({ ...newAppointment, date: format(day, 'yyyy-MM-dd'), time: hour });
                      setShowModal(true);
                    }}
                    className="min-h-12 border-l border-[var(--nova-border-subtle)] p-1 hover:bg-[var(--nova-surface)] cursor-pointer transition-colors"
                  >
                    {dayAppointments.map((apt, j) => {
                      const service = services.find(s => s.id === apt.service_id);
                      const client = clients.find(c => c.id === apt.client_id);
                      return (
                        <div
                          key={j}
                          className="text-xs p-1.5 rounded-lg mb-1 font-medium"
                          style={{ 
                            backgroundColor: `${service?.color || '#6366f1'}15`,
                            color: service?.color || '#6366f1'
                          }}
                        >
                          <div className="font-semibold truncate">{client?.name || 'Cliente'}</div>
                          <div className="opacity-70 truncate">{service?.name}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 14 }, (_, i) => `${(i + 7).toString().padStart(2, '0')}:00`);
    const dayAppointments = getAppointmentsForDate(currentDate);

    return (
      <div className="nova-card overflow-hidden">
        <div className="p-5 border-b border-[var(--nova-border)] text-center">
          <div className={`text-2xl font-bold ${isToday(currentDate) ? 'text-[var(--nova-primary-light)]' : 'text-white'}`}>
            {format(currentDate, 'd')}
          </div>
          <div className="text-caption">{format(currentDate, 'EEEE, MMMM yyyy', { locale: es })}</div>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {hours.map((hour) => {
            const hourAppointments = dayAppointments.filter(apt => apt.time.startsWith(hour.split(':')[0]));
            return (
              <div key={hour} className="flex border-b border-[var(--nova-border-subtle)]">
                <div className="w-20 p-3 text-sm text-[var(--nova-text-quaternary)] text-right font-medium">{hour}</div>
                <div 
                  className="flex-1 min-h-16 p-2 hover:bg-[var(--nova-surface)] cursor-pointer transition-colors"
                  onClick={() => {
                    setNewAppointment({ ...newAppointment, date: format(currentDate, 'yyyy-MM-dd'), time: hour });
                    setShowModal(true);
                  }}
                >
                  {hourAppointments.map((apt, j) => {
                    const service = services.find(s => s.id === apt.service_id);
                    const client = clients.find(c => c.id === apt.client_id);
                    const staffMember = staff.find(s => s.id === apt.staff_id);
                    return (
                      <div
                        key={j}
                        className="p-3 rounded-xl mb-2 flex items-center justify-between group"
                        style={{ 
                          backgroundColor: `${service?.color || '#6366f1'}12`,
                          borderLeft: `3px solid ${service?.color || '#6366f1'}`
                        }}
                      >
                        <div>
                          <div className="font-semibold text-[var(--nova-text)]">{client?.name || 'Cliente'}</div>
                          <div className="text-sm text-[var(--nova-text-secondary)]">{service?.name} &middot; {apt.time}</div>
                          {staffMember && (
                            <div className="text-xs text-[var(--nova-text-tertiary)] mt-1">Con {staffMember.name}</div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAppointment(apt.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/10 rounded-lg transition-all"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <motion.div 
        data-testid="agenda-page" 
        className="space-y-8"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item} className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-headline text-white">Agenda</h1>
            <p className="text-caption mt-1">{appointments.length} citas programadas</p>
          </div>
          <button
            onClick={() => {
              setSelectedDate(new Date());
              setNewAppointment({ ...newAppointment, date: format(new Date(), 'yyyy-MM-dd') });
              setShowModal(true);
            }}
            data-testid="add-appointment-button"
            className="nova-btn nova-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nueva Cita
          </button>
        </motion.div>

        {/* Navigation */}
        <motion.div variants={item} className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button onClick={handlePrev} className="p-2 hover:bg-[var(--nova-surface)] rounded-xl transition-colors">
              <ChevronLeft className="w-5 h-5 text-[var(--nova-text-secondary)]" />
            </button>
            <button 
              onClick={() => setCurrentDate(new Date())} 
              className="px-4 py-1.5 text-sm font-medium text-[var(--nova-text-secondary)] hover:text-white hover:bg-[var(--nova-surface)] rounded-xl transition-all"
            >
              Hoy
            </button>
            <button onClick={handleNext} className="p-2 hover:bg-[var(--nova-surface)] rounded-xl transition-colors">
              <ChevronRight className="w-5 h-5 text-[var(--nova-text-secondary)]" />
            </button>
            <h2 className="text-title text-white ml-2">
              {view === 'day' 
                ? format(currentDate, "d 'de' MMMM, yyyy", { locale: es })
                : format(currentDate, "MMMM yyyy", { locale: es })
              }
            </h2>
          </div>
          <div className="flex bg-[var(--nova-surface)] border border-[var(--nova-border)] rounded-xl p-1">
            {views.map((v) => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={`px-5 py-1.5 text-sm font-medium rounded-lg transition-all ${
                  view === v.id 
                    ? 'bg-[var(--nova-primary)] text-white shadow-lg shadow-indigo-500/20' 
                    : 'text-[var(--nova-text-tertiary)] hover:text-white'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Calendar */}
        <motion.div variants={item}>
          {loading ? (
            <div className="h-96 nova-skeleton rounded-xl" />
          ) : (
            <motion.div
              key={view}
              initial={{ opacity: 0, scale: 0.99 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {view === 'month' && renderMonthView()}
              {view === 'week' && renderWeekView()}
              {view === 'day' && renderDayView()}
            </motion.div>
          )}
        </motion.div>

        {/* Create Appointment Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)] text-[var(--nova-text)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-title">Nueva Cita</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Cliente *</label>
                <Select value={newAppointment.client_id} onValueChange={(v) => setNewAppointment({ ...newAppointment, client_id: v })}>
                  <SelectTrigger className="w-full bg-[var(--nova-bg-subtle)] border-[var(--nova-border)] text-[var(--nova-text)]" data-testid="apt-client-select">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)] max-h-60">
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Servicio *</label>
                <Select value={newAppointment.service_id} onValueChange={(v) => setNewAppointment({ ...newAppointment, service_id: v })}>
                  <SelectTrigger className="w-full bg-[var(--nova-bg-subtle)] border-[var(--nova-border)] text-[var(--nova-text)]" data-testid="apt-service-select">
                    <SelectValue placeholder="Seleccionar servicio" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)]">
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} - ${s.price}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Personal</label>
                <Select value={newAppointment.staff_id} onValueChange={(v) => setNewAppointment({ ...newAppointment, staff_id: v })}>
                  <SelectTrigger className="w-full bg-[var(--nova-bg-subtle)] border-[var(--nova-border)] text-[var(--nova-text)]">
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)]">
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Fecha *</label>
                  <input
                    type="date"
                    value={newAppointment.date}
                    onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })}
                    data-testid="apt-date"
                    className="nova-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Hora *</label>
                  <input
                    type="time"
                    value={newAppointment.time}
                    onChange={(e) => setNewAppointment({ ...newAppointment, time: e.target.value })}
                    data-testid="apt-time"
                    className="nova-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Notas</label>
                <textarea
                  value={newAppointment.notes}
                  onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                  className="nova-input h-20 resize-none"
                  placeholder="Notas adicionales..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="nova-btn nova-btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateAppointment}
                  disabled={saving}
                  data-testid="save-appointment-button"
                  className="nova-btn nova-btn-primary flex-1"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Guardar
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </motion.div>
    </Layout>
  );
}
